import type { EmployeeSyncInfoDto, EmployeeVendorInfoDto, TeamMemberDto, TeamMemberUserDto } from '@pimm/services/lib/sms-workforce/types';

export type DailyHoursShape = {
  dayOfWeek: string;
  isClosed: boolean;
  open24Hrs: boolean;
  servesBreakfast: boolean;
  hasDiningRoom: boolean;
  hasPickupWindow: boolean;
  diningRmOpen: string;
  diningRmClose: string;
  driveThruOpen: string;
  driveThruClose: string;
  breakfastStart: string;
  breakfastEnd: string;
  lunchStart: string;
  lunchEnd: string;
  dinnerStart: string;
  dinnerEnd: string;
  storeOpen: string;
  storeClose: string;
};

export type EditValuesDay = {
  diningRmOpen: string;
  diningRmClose: string;
  driveThruOpen: string;
  driveThruClose: string;
  breakfastStart: string;
  breakfastEnd: string;
  lunchStart: string;
  lunchEnd: string;
  dinnerStart: string;
  dinnerEnd: string;
};

export type EditValues = EditValuesDay[];

export type ActiveCell = { rowKey: string; dayIdx: number };

export type ConfirmModalState = { type: 'discard' | 'exit' | 'nav'; action?: () => void };

// Grid dimension constants
export const LABEL_W = 176;
export const CELL_W_FALLBACK = 120;
export const ROW_H = 52;
export const HEADER_H = 40;

export const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
export const DAYS_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export const EDITABLE_ROW_CONFIG: Record<
  string,
  {
    label: string;
    openField: keyof EditValuesDay;
    closeField: keyof EditValuesDay;
    enabledField: keyof DailyHoursShape | null;
    openLabel: string;
    closeLabel: string;
    noServiceLabel: string;
    ineligibleReason?: string;
  }
> = {
  diningRoom: {
    label: 'store_settings:dining_room',
    openField: 'diningRmOpen',
    closeField: 'diningRmClose',
    enabledField: 'hasDiningRoom',
    openLabel: 'store_settings:open',
    closeLabel: 'store_settings:close',
    noServiceLabel: 'store_settings:closed',
    ineligibleReason: 'store_settings:no_dining_room',
  },
  driveThru: {
    label: 'store_settings:drive_thru_puw',
    openField: 'driveThruOpen',
    closeField: 'driveThruClose',
    enabledField: 'hasPickupWindow',
    openLabel: 'store_settings:open',
    closeLabel: 'store_settings:close',
    noServiceLabel: 'store_settings:closed',
    ineligibleReason: 'store_settings:no_drive_thru',
  },
  breakfast: {
    label: 'store_settings:breakfast_hours',
    openField: 'breakfastStart',
    closeField: 'breakfastEnd',
    enabledField: 'servesBreakfast',
    openLabel: 'store_settings:start',
    closeLabel: 'store_settings:end',
    noServiceLabel: 'store_settings:no_service',
    ineligibleReason: 'store_settings:breakfast_not_served',
  },
  lunch: {
    label: 'store_settings:lunch_hours',
    openField: 'lunchStart',
    closeField: 'lunchEnd',
    enabledField: null,
    openLabel: 'store_settings:start',
    closeLabel: 'store_settings:end',
    noServiceLabel: 'store_settings:no_service',
  },
  dinner: {
    label: 'store_settings:dinner_hours',
    openField: 'dinnerStart',
    closeField: 'dinnerEnd',
    enabledField: null,
    openLabel: 'store_settings:start',
    closeLabel: 'store_settings:end',
    noServiceLabel: 'store_settings:no_service',
  },
};

// ─── Equipment Profile types ────────────────────────────────────────────────

export type StringTranslation = {
  culture: string;
  text: string;
};

export type EquipmentProfileItemDto = {
  inventoryItemId: string;
  title: string;
  description: string | null;
  key: string;
  quantity: number;
  quantityMin: number;
  quantityMax: number;
  defaultQuantity: number;
  isOverridden: boolean;
  isCustomerEditable: boolean;
  category: {
    id: number;
    title: string;
    subTitle: string | null;
    key: string;
    seq: number;
    titleTranslations: StringTranslation[];
    subTitleTranslations: StringTranslation[];
  };
};

export type CategoryGroup = {
  categoryId: number;
  categoryTitle: string;
  categorySubTitle: string | null;
  categorySeq: number;
  items: EquipmentProfileItemDto[];
};

export type PageGroup = {
  pagePrefix: string;
  pageLabel: string;
  categories: CategoryGroup[];
};

// ─── Team Management types ────────────────────────────────────────────────

// `GetTeamMembersResponse.syncInfo` is typed `EmployeeSyncInfoDto` in the generated client —
// structurally identical to (but a distinct type from) the `SyncInfoDto` used on the Sales &
// Labor payload. Re-export under the name the rest of this module uses.
export type { EmployeeSyncInfoDto as SyncInfoDto, EmployeeVendorInfoDto as VendorInfoDto };

// role / userRole / user.status ride on this endpoint as plain `string` in the generated DTOs
// (no server-side enum) — these literal unions are ours, not generated. Values confirmed
// against the live sms-workforce swagger schema (2026-07-08).
export type TeamMemberRole = 'SMS EMPLOYEE' | 'SMS USER' | 'SMS MANAGER';
export type TeamMemberStatus = 'Active' | 'Inactive' | 'Terminated';
export type ImportSource = 'Importer' | 'Manual';
export type ImportSyncMode = 'Auto' | 'Protected' | 'Locked';
export type UserLoginStatus = 'Active' | 'Pending' | 'Disabled';

// CONFIRMED against a real response for a known-verified user (868ka69j4):
// `"recoveryEmailStatus": "Verified"` — the live API serializes this as a
// NAMED STRING, not the raw integer the generated client's `RecoveryEmailStatusEnum`
// declares. That mismatch meant every `=== RECOVERY_EMAIL_STATUS_VERIFIED` check
// (Recovery Email column, hasNoRecoveryEmail drift, Employee Profile modal, print
// view) compared a string to a number and was always false. Name order matches the
// Team Roster handoff doc (ClickUp doc 12f9mz-52571, PR 1): NotSet /
// PendingVerification / Verified / VerificationExpired.
export type RecoveryEmailStatus = 'NotSet' | 'PendingVerification' | 'Verified' | 'VerificationExpired';
export const RECOVERY_EMAIL_STATUS_VERIFIED: RecoveryEmailStatus = 'Verified';

// Derived from the real generated DTOs (not hand-rolled) so backend field additions/removals
// surface as type errors here instead of silently drifting. `role`/`status`/`importSource`/
// `importSyncMode`/`user` are narrowed from the generated optional/plain-string shape to the
// literal unions above, since downstream code treats them as always-present for a real row.
export type TeamMemberUser = Omit<TeamMemberUserDto, 'status' | 'userRole' | 'recoveryEmailStatus'> & {
  // Independent of Employee.role — synced via integration but can drift.
  userRole: TeamMemberRole;
  // Independent of Employee.status — drift detection compares the two.
  status: UserLoginStatus;
  recoveryEmailStatus?: RecoveryEmailStatus;
};

export type TeamMember = Omit<TeamMemberDto, 'role' | 'status' | 'importSource' | 'importSyncMode' | 'user'> & {
  role: TeamMemberRole;
  status: TeamMemberStatus;
  // Tri-state import sync mode. Default 'Auto' for all new employees; server
  // sets 'Protected' automatically on any opsbee UI edit; 'Locked' = full freeze.
  importSyncMode: ImportSyncMode;
  // Origin of this Employee record — permanent, does not change when importSyncMode changes.
  importSource: ImportSource;
  user: TeamMemberUser | undefined;
};

export type RoleFilterValue = Set<TeamMemberRole>;

export type ViewMode = 'team' | 'users';

export type DriftKind =
  | 'statusMismatch'
  | 'roleMismatch'
  | 'staleSchedule'
  | 'overduePurge'
  | 'staleLogin'
  | 'noPinSet'
  | 'noContactEmail'
  | 'noRecoveryEmail';

// Team tab's Issues filter excludes Stale Login — it's a Users-only signal (2026-07-05 change).
export const TEAM_DRIFT_KINDS: DriftKind[] = ['statusMismatch', 'roleMismatch', 'staleSchedule', 'overduePurge'];
export const USERS_DRIFT_KINDS: DriftKind[] = ['roleMismatch', 'staleLogin', 'noPinSet', 'noContactEmail', 'noRecoveryEmail'];

export type StatusFilterValue = 'Active' | 'NotActive' | { kind: 'drift'; drift: DriftKind };

export const ROLE_DISPLAY_PRIORITY: Record<string, number> = {
  'SMS MANAGER': 0,
  'SMS USER': 1,
  'SMS EMPLOYEE': 2,
};
